import PropTypes from 'prop-types';
import { Chip, Card, Stack, Typography, Divider } from '@mui/material';
import Label from 'src/components/label';
import { useEffect, useState } from 'react';
import EmptyContent from 'src/components/empty-content';
import { SCREENING_STATUS } from 'src/constants/job-candidate';
import Scrollbar from 'src/components/scrollbar';

const ProfileMatch = ({ candidateDetails, jobDetails }) => {
  // states
  const [criteriaKeys, setCriteriaKeys] = useState([]);
  const [message, setMessage] = useState('');

  // effects
  useEffect(() => {
    if (candidateDetails && candidateDetails.criteria_match) {
      const _criteriaKeys = Object.keys(
        candidateDetails.criteria_match.criteria_wise_scores,
      );
      setCriteriaKeys(_criteriaKeys);
    }
  }, [candidateDetails, candidateDetails.criteria_match]);

  useEffect(() => {
    if (candidateDetails.screening_status !== null) {
      if (SCREENING_STATUS.ANALYZING === candidateDetails.screening_status) {
        setMessage('AI is analyzing resume. Please check after sometime');
      } else if (
        SCREENING_STATUS.SCREENING_ERROR === candidateDetails.screening_status
      ) {
        setMessage('Found error. Please check after sometime');
      }
    }
  }, [candidateDetails]);

  return candidateDetails?.candidate_info?.profile_blob ? (
    <Stack spacing={1}>
      <Stack>
        <Card sx={{ p: 3 }}>
          {message ? (
            <Typography variant="h6">{message}</Typography>
          ) : (
            <>
              {(candidateDetails.criteria_match ||
                candidateDetails.skill_match) && (
                <Stack spacing={4}>
                  {candidateDetails.criteria_match && (
                    <>
                      <Stack spacing={1}>
                        <Typography variant="h5">
                          Remarks on Preferred Qualifying Criteria
                        </Typography>
                        <Stack spacing={3}>
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography variant="body1" fontWeight={600}>
                                AI Recommendation :
                              </Typography>
                              <Label
                                color={
                                  candidateDetails.criteria_match.selected
                                    ? 'success'
                                    : 'warning'
                                }
                              >
                                {candidateDetails.criteria_match.selected
                                  ? 'Eligible'
                                  : 'Not Eligible'}
                              </Label>
                            </Stack>
                            <Typography variant="body1">
                              {candidateDetails.criteria_match.match_remark}
                            </Typography>
                          </Stack>
                          <Stack spacing={0.5}>
                            <Typography variant="h6" fontWeight={600}>
                              Preferred Qualifying Criteria
                            </Typography>
                            {criteriaKeys &&
                              criteriaKeys.map(key => {
                                const criteria =
                                  candidateDetails.criteria_match
                                    .criteria_wise_scores[key];
                                return (
                                  <Stack>
                                    <Typography
                                      variant="body1"
                                      fontWeight={600}
                                    >
                                      Criteria {key} : {criteria.name}
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                      <Typography
                                        variant="body1"
                                        whiteSpace="nowrap"
                                        fontWeight={600}
                                      >
                                        Remark :
                                      </Typography>
                                      <Typography variant="body1">
                                        {criteria.remark}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                );
                              })}
                          </Stack>
                        </Stack>
                      </Stack>
                      <Divider />
                    </>
                  )}
                  {candidateDetails.skill_match && (
                    <Stack spacing={1}>
                      <Typography variant="h5">
                        Remarks on Job Details
                      </Typography>
                      <Stack spacing={3}>
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography variant="body1" fontWeight={600}>
                              AI Recommendation :
                            </Typography>
                            <Label
                              color={
                                candidateDetails.skill_match.selected
                                  ? 'success'
                                  : 'warning'
                              }
                            >
                              {candidateDetails.skill_match.selected
                                ? 'Eligible'
                                : 'Not Eligible'}
                            </Label>
                          </Stack>
                          <Typography variant="body1">
                            {candidateDetails.skill_match.match_remark}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              )}
            </>
          )}
        </Card>
      </Stack>
      <Stack direction="row" spacing={1}>
        {candidateDetails.candidate_info.profile_blob && (
          <Stack width="50%" height="85vh">
            <Card sx={{ p: 3 }}>
              <Scrollbar>
                <Stack spacing={3}>
                  {candidateDetails.candidate_info.profile_blob
                    .split('\n\n')
                    .map((section, index) => (
                      <Stack key={index}>
                        {section.split('\n').map((line, i) => (
                          <Typography variant="body1" key={i}>
                            {line}
                          </Typography>
                        ))}
                      </Stack>
                    ))}
                </Stack>
              </Scrollbar>
            </Card>
          </Stack>
        )}
        <Stack
          width={candidateDetails.candidate_info.profile_blob ? '50%' : '100%'}
          height="85vh"
        >
          <Card sx={{ p: 3, height: '100%' }}>
            <Scrollbar>
              <Stack spacing={2}>
                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                  <Typography variant="h4">{jobDetails.name}</Typography>
                </Stack>

                <Stack spacing={2}>
                  <Typography variant="h6">Skills</Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ flexWrap: 'wrap' }}
                    spacing={1}
                  >
                    {jobDetails.skills?.map(skill => (
                      <Chip key={skill} label={skill} variant="soft" />
                    ))}
                  </Stack>
                </Stack>
                <Stack>
                  <Typography variant="h6">Experience</Typography>
                  <Typography>{`${jobDetails.min_experience}-${jobDetails.max_experience} years`}</Typography>
                </Stack>
                <Stack>
                  <Typography variant="h6">Job Description</Typography>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: jobDetails.job_description,
                    }}
                  />
                </Stack>
              </Stack>
            </Scrollbar>
          </Card>
        </Stack>
      </Stack>
    </Stack>
  ) : (
    <Card sx={{ p: 3 }}>
      <EmptyContent
        filled
        title="No resume found, please upload resume to see the profile match analysis"
        sx={{
          py: 10,
        }}
      />
    </Card>
  );
};

ProfileMatch.propTypes = {
  candidateDetails: PropTypes.object,
  jobDetails: PropTypes.object,
};

export default ProfileMatch;
